import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import ReportModal from '../components/ReportModal'
import { useAuth, displayUser } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { supabase } from '../lib/supabase'
import { decodeEntities } from '../lib/decodeEntities'
import SEO from '../components/SEO'

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

const COVER_GRADIENTS = [
  'linear-gradient(135deg, #1E0A3C 0%, #3B0764 35%, #831843 100%)',
  'linear-gradient(135deg, #0F4C75 0%, #1E0A3C 50%, #3B0764 100%)',
  'linear-gradient(135deg, #064E3B 0%, #0F4C75 50%, #1E0A3C 100%)',
  'linear-gradient(135deg, #3B0764 0%, #831843 50%, #1D4ED8 100%)',
  'linear-gradient(135deg, #831843 0%, #FF6B35 50%, #3B0764 100%)',
  'linear-gradient(135deg, #1D4ED8 0%, #7C3AED 50%, #EC4899 100%)',
]

function formatNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

// ── Screens ────────────────────────────────────────────────────────────────────

function IntroScreen({ quiz, onStart, saved, onSave, saveBusy, onReport, onShare, copied, onHide, hideBusy, isAdmin }) {
  const coverGradient = COVER_GRADIENTS[quiz.coverIdx ?? 0]
  const coverStyle = quiz.coverUrl
    ? { backgroundImage: `url(${quiz.coverUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: coverGradient }
  return (
    <div className="qz-intro">
      <div className="qz-intro__cover" style={coverStyle} />
      <div className="qz-intro__body">
        <span className={`qz-type-badge qz-type-badge--${quiz.type}`}>
          {quiz.type === 'personality' ? '🌀 Personality quiz' : '⚡ Knowledge quiz'}
        </span>
        <h1 className="qz-intro__title">{decodeEntities(quiz.title)}</h1>
        {quiz.description && <p className="qz-intro__desc">{decodeEntities(quiz.description)}</p>}
        <div className="qz-intro__meta">
          <span>By <Link to={`/user/${quiz.author}`} className="qz-intro__author">{decodeEntities(quiz.author)}</Link></span>
          <span>·</span>
          <span>{quiz.questions.length} questions</span>
          <span>·</span>
          <span>▶ {formatNum(quiz.plays)} plays</span>
        </div>
        <div className="qz-intro__actions">
          <button className="btn btn-primary qz-start-btn" onClick={onStart}>
            Start quiz
          </button>
          <button className="btn story-header__action-btn" onClick={onShare}>
            {copied ? '✓ Copied!' : '↗ Share'}
          </button>
          {onReport && (
            <button className="qz-report-btn" onClick={onReport} title="Report this quiz">
              Report
            </button>
          )}
          {isAdmin && onHide && (
            <button
              className="btn btn-outline"
              style={{ fontSize: '0.8rem', color: quiz.hidden ? '#16A34A' : '#DC2626', borderColor: quiz.hidden ? '#16A34A' : '#DC2626' }}
              disabled={hideBusy}
              onClick={onHide}
            >
              {hideBusy ? '…' : quiz.hidden ? 'Unhide' : 'Hide'}
            </button>
          )}
          {onSave && (
            <button
              className={`qz-save-btn${saved ? ' qz-save-btn--saved' : ''}`}
              onClick={onSave}
              disabled={saveBusy}
              title={saved ? 'Remove from saved' : 'Save quiz'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              {saved ? 'Saved' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function QuestionScreen({ quiz, questionIdx, selected, onSelect, onConfirm, confirmed, onNext, onBack, isRevisit, displayAnswers }) {
  const question   = quiz.questions[questionIdx]
  const total      = quiz.questions.length
  const progress   = ((questionIdx) / total) * 100
  const isLast     = questionIdx === total - 1
  const multiCorrect = question.multiCorrect ?? false
  const canGoBack  = questionIdx > 0
  const answersForRender = displayAnswers ?? question.answers

  // For multi-answer: selected is an array; "has answer" means confirmed
  // For single-answer: selected is a single id; "has answer" once any option clicked
  const hasAnswer = multiCorrect ? confirmed : selected !== null && !Array.isArray(selected)
  // Lock answer buttons only during multi-correct first-pass after Confirm.
  // Single-answer is always editable until user clicks Next; revisited multi
  // questions stay editable so users can change their pick after Back.
  const locked = multiCorrect && confirmed && !isRevisit
  const selectedSet = multiCorrect
    ? new Set(Array.isArray(selected) ? selected : [])
    : new Set(selected != null ? [selected] : [])

  return (
    <div className="qz-question-screen">
      {/* Progress */}
      <div className="qz-progress-bar">
        <div className="qz-progress-bar__fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="qz-progress-label">{questionIdx + 1} / {total}</div>

      <div className="qz-question-wrap">
        <h2 className="qz-question-text">{decodeEntities(question.text)}</h2>
        {multiCorrect && !hasAnswer && (
          <p className="qz-multi-hint">Select all correct answers</p>
        )}

        <div className="qz-answers">
          {answersForRender.map(a => {
            let cls = 'qz-answer'
            const isSelected = selectedSet.has(a.id)
            if (isSelected) {
              cls += ' qz-answer--selected'
            }
            return (
              <button
                key={a.id}
                type="button"
                className={cls}
                onClick={() => !locked && onSelect(a.id)}
                disabled={locked}
              >
                {multiCorrect && !locked && (
                  <span className="qz-answer__checkbox">{isSelected ? '☑' : '☐'}</span>
                )}
                {decodeEntities(a.text)}
              </button>
            )
          })}
        </div>

        {multiCorrect && !confirmed && selectedSet.size > 0 && (
          <div className="qz-next-wrap">
            <button className="btn btn-primary qz-next-btn" onClick={onConfirm}>
              Confirm
            </button>
          </div>
        )}

        <div className="qz-nav-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 12 }}>
          {canGoBack ? (
            <button type="button" className="btn btn-outline qz-back-btn" onClick={onBack}>
              ← Back
            </button>
          ) : <span />}
          {hasAnswer ? (
            <button className="btn btn-primary qz-next-btn" onClick={onNext}>
              {isLast ? 'See results' : 'Next →'}
            </button>
          ) : <span />}
        </div>
      </div>
    </div>
  )
}

function ResultsScreen({ quiz, serverResult, saved, onSave, saveBusy }) {
  if (serverResult?.type === 'knowledge') {
    const { score: correct, total, pct, results } = serverResult

    let verdict = ''
    if (pct === 100) verdict = '🏆 Perfect score!'
    else if (pct >= 80)  verdict = '🌟 Great job!'
    else if (pct >= 60)  verdict = '👍 Not bad!'
    else if (pct >= 40)  verdict = '📚 Keep studying!'
    else                 verdict = '💪 Better luck next time!'

    const resultMap = Object.fromEntries((results ?? []).map(r => [r.questionId, r]))

    return (
      <div className="qz-results">
        <div className="qz-results__score-ring">
          <span className="qz-results__score-num">{correct}</span>
          <span className="qz-results__score-den">/{total}</span>
        </div>
        <div className="qz-results__pct">{pct}%</div>
        <div className="qz-results__verdict">{verdict}</div>
        <h2 className="qz-results__title">{decodeEntities(quiz.title)}</h2>

        {/* Per-question review */}
        <div className="qz-review">
          {quiz.questions.map((q, i) => {
            const r = resultMap[q.id]
            if (!r) return null
            const correctIdSet = new Set(r.correctAnswerIds)
            const selectedIdSet = new Set(r.selectedAnswerIds)
            const yourAnswers = q.answers.filter(a => selectedIdSet.has(a.id))
            const correctAnswers = q.answers.filter(a => correctIdSet.has(a.id))
            return (
              <div key={q.id} className={`qz-review-item${r.correct ? ' qz-review-item--correct' : ' qz-review-item--wrong'}`}>
                <span className="qz-review-item__icon">{r.correct ? '✓' : '✗'}</span>
                <div>
                  <div className="qz-review-item__q">Q{i + 1}: {decodeEntities(q.text)}</div>
                  {!r.correct && <div className="qz-review-item__yours">Your answer: {decodeEntities(yourAnswers.map(a => a.text).join(', ')) || '—'}</div>}
                  {!r.correct && <div className="qz-review-item__correct">Correct: {decodeEntities(correctAnswers.map(a => a.text).join(', '))}</div>}
                </div>
              </div>
            )
          })}
        </div>

        <div className="qz-results__actions">
          <Link to="/quizzes" className="btn btn-outline">More quizzes</Link>
          {onSave && (
            <button className={`qz-save-btn${saved ? ' qz-save-btn--saved' : ''}`} onClick={onSave} disabled={saveBusy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              {saved ? 'Saved' : 'Save quiz'}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Personality result
  const outcome = serverResult?.outcome

  return (
    <div className="qz-results">
      <div className="qz-results__personality-label">You are…</div>
      <h2 className="qz-results__personality-title">{decodeEntities(outcome?.title) ?? 'Unknown'}</h2>
      {outcome?.description && <p className="qz-results__personality-desc">{decodeEntities(outcome.description)}</p>}
      <div className="qz-results__quiz-title">from "{decodeEntities(quiz.title)}"</div>

      <div className="qz-results__actions">
        <Link to="/quizzes" className="btn btn-outline">More quizzes</Link>
        {onSave && (
          <button className={`qz-save-btn${saved ? ' qz-save-btn--saved' : ''}`} onClick={onSave} disabled={saveBusy}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
            </svg>
            {saved ? 'Saved' : 'Save quiz'}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Quiz comments ─────────────────────────────────────────────────────────────

function ResultBadge({ snapshot }) {
  if (!snapshot) return null
  if (snapshot.type === 'knowledge') {
    return (
      <div className="qz-comment__result qz-comment__result--knowledge">
        <span className="qz-comment__result-score">{snapshot.score}/{snapshot.total}</span>
        <span className="qz-comment__result-pct">{snapshot.pct}%</span>
        <span className="qz-comment__result-verdict">{snapshot.verdict}</span>
      </div>
    )
  }
  return (
    <div className="qz-comment__result qz-comment__result--personality">
      <span className="qz-comment__result-label">Got:</span>
      <span className="qz-comment__result-outcome">{snapshot.outcome}</span>
    </div>
  )
}

function QuizCommentsSection({ quizId, user, profile, resultSnapshot }) {
  const [comments, setComments]       = useState([])
  const [loading, setLoading]         = useState(true)
  const [text, setText]               = useState('')
  const [includeResult, setIncludeResult] = useState(true)
  const [posting, setPosting]         = useState(false)
  const [postError, setPostError]     = useState('')
  const [deleting, setDeleting]       = useState(null)
  const [menuOpenId, setMenuOpenId]   = useState(null)
  const [editingId, setEditingId]     = useState(null)
  const [editText, setEditText]       = useState('')
  const [editError, setEditError]     = useState('')
  const [saving, setSaving]           = useState(false)
  const [replyingToId, setReplyingToId] = useState(null)
  const [replyText, setReplyText]       = useState('')
  const [replyPosting, setReplyPosting] = useState(false)
  const [replyError, setReplyError]     = useState('')
  const [reportingCommentId, setReportingCommentId] = useState(null)
  const [likedSet, setLikedSet] = useState(new Set())
  const [likeCounts, setLikeCounts] = useState({})
  const formRef = useRef(null)

  useEffect(() => {
    if (!menuOpenId) return
    function handler() { setMenuOpenId(null) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpenId])

  useEffect(() => {
    axios.get(`/api/quizzes/${quizId}/comments`)
      .then(r => {
        setComments(r.data)
        const counts = {}
        r.data.forEach(c => { counts[c.id] = c.likes ?? 0 })
        setLikeCounts(counts)
        setTimeout(() => {
          const hash = window.location.hash
          if (hash) {
            const el = document.getElementById(hash.slice(1))
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('highlight-flash') }
          }
        }, 300)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [quizId])

  useEffect(() => {
    function onNotificationNavigate() {
      const hash = window.location.hash
      if (!hash) return
      const el = document.getElementById(hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('highlight-flash')
      }
    }
    window.addEventListener('verse:notification-navigate', onNotificationNavigate)
    return () => window.removeEventListener('verse:notification-navigate', onNotificationNavigate)
  }, [])

  // Batch fetch which comments the user has liked
  useEffect(() => {
    if (!user || !comments.length) return
    async function fetchLiked() {
      try {
        const headers = await getHeaders()
        const { data } = await axios.post('/api/quizzes/comments/liked-batch', { commentIds: comments.map(c => c.id) }, { headers })
        setLikedSet(new Set(data.likedIds ?? []))
      } catch (err) { console.error('[QuizPlayerPage] Failed to fetch comment likes:', err) }
    }
    fetchLiked()
  }, [comments.length, user])

  async function handleLike(commentId) {
    if (!user) return
    const wasLiked = likedSet.has(commentId)
    setLikedSet(prev => {
      const next = new Set(prev)
      if (wasLiked) next.delete(commentId); else next.add(commentId)
      return next
    })
    setLikeCounts(prev => ({ ...prev, [commentId]: (prev[commentId] ?? 0) + (wasLiked ? -1 : 1) }))
    try {
      const headers = await getHeaders()
      if (wasLiked) await axios.delete(`/api/quizzes/comments/${commentId}/like`, { headers })
      else await axios.post(`/api/quizzes/comments/${commentId}/like`, {}, { headers })
    } catch {
      setLikedSet(prev => {
        const next = new Set(prev)
        if (wasLiked) next.add(commentId); else next.delete(commentId)
        return next
      })
      setLikeCounts(prev => ({ ...prev, [commentId]: (prev[commentId] ?? 0) + (wasLiked ? 1 : -1) }))
    }
  }

  async function handlePost(e) {
    e.preventDefault()
    if (!text.trim() || posting) return
    setPosting(true)
    setPostError('')
    try {
      const headers = await getHeaders()
      const body = { content: text }
      if (resultSnapshot && includeResult) body.result_snapshot = resultSnapshot
      const { data } = await axios.post(`/api/quizzes/${quizId}/comments`, body, { headers })
      setComments(prev => [data, ...prev])
      setText('')
    } catch (err) {
      setPostError(err.response?.data?.error || 'Failed to post.')
    } finally {
      setPosting(false)
    }
  }

  async function handleSaveEdit(id) {
    if (!editText.trim() || saving) return
    setSaving(true)
    setEditError('')
    try {
      const headers = await getHeaders()
      const { data } = await axios.put(`/api/quizzes/${quizId}/comments/${id}`, { content: editText }, { headers })
      setComments(prev => prev.map(c => c.id === id ? { ...c, content: data.content } : c))
      setEditingId(null)
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id) {
    setDeleting(id)
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/quizzes/${quizId}/comments/${id}`, { headers })
      setComments(prev => prev.filter(c => c.id !== id))
    } catch (err) { console.error('[QuizPlayerPage] Failed to delete comment:', err) } finally {
      setDeleting(null)
    }
  }

  async function handleReply(commentId) {
    if (!replyText.trim() || replyPosting) return
    setReplyPosting(true)
    setReplyError('')
    try {
      const headers = await getHeaders()
      // Flatten at 2 levels: if replying to a reply, group under the same parent
      const target = comments.find(c => c.id === commentId)
      const parentId = target?.parent_id ? target.parent_id : commentId
      const replyToId = target?.parent_id ? commentId : undefined
      const { data } = await axios.post(`/api/quizzes/${quizId}/comments`, { content: replyText, parent_id: parentId, reply_to_id: replyToId }, { headers })
      // Enrich with reply_to_username for immediate display
      if (replyToId && target?.profile?.username) {
        data.reply_to_username = target.profile.username
      }
      setComments(prev => [...prev, data])
      setReplyText('')
      setReplyingToId(null)
    } catch (err) {
      setReplyError(err.response?.data?.error || 'Failed to post reply.')
    } finally {
      setReplyPosting(false)
    }
  }

  function formatTime(iso) {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const myAvatarUrl = profile?.avatarUrl ?? null
  const myGrad = profile?.avatarGrad ?? 'linear-gradient(135deg, #7C3AED, #EC4899)'
  const myInitial = user?.email?.[0]?.toUpperCase() ?? '?'

  return (
    <section className="qz-comments">
      <h3 className="qz-comments__title">Comments <span className="qz-comments__count">{comments.length}</span></h3>

      {user ? (
        <form className="qz-comment-form" onSubmit={handlePost} ref={formRef}>
          <div className="qz-comment-form__avatar" style={{ background: myAvatarUrl ? undefined : myGrad }}>
            {myAvatarUrl
              ? <img src={myAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : myInitial}
          </div>
          <div className="qz-comment-form__body">
            {postError && <div className="qz-comment-form__error">{postError}</div>}
            <textarea
              className="qz-comment-form__input"
              placeholder="Share your thoughts…"
              value={text}
              onChange={e => { setText(e.target.value); setPostError('') }}
              rows={3}
            />
            <div className="qz-comment-form__footer">
              {resultSnapshot && (
                <label className="qz-comment-form__result-toggle">
                  <input
                    type="checkbox"
                    checked={includeResult}
                    onChange={e => setIncludeResult(e.target.checked)}
                  />
                  Share my result
                </label>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                style={{ padding: '7px 18px', fontSize: '0.85rem' }}
                disabled={!text.trim() || posting}
              >
                {posting ? 'Posting…' : 'Post'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <p className="qz-comments__login"><Link to="/login">Sign in</Link> to leave a comment.</p>
      )}

      <div className="qz-comment-list">
        {loading && <p className="qz-comments__empty">Loading…</p>}
        {!loading && comments.filter(c => !c.parent_id).length === 0 && <p className="qz-comments__empty">No comments yet. Be the first!</p>}
        {comments.filter(c => !c.parent_id).map(c => {
          const replies = comments.filter(r => r.parent_id === c.id)
          return (
            <QzCommentItem
              key={c.id}
              c={c}
              replies={replies}
              allComments={comments}
              user={user}
              editingId={editingId}
              editText={editText}
              setEditText={setEditText}
              editError={editError}
              saving={saving}
              deleting={deleting}
              menuOpenId={menuOpenId}
              setMenuOpenId={setMenuOpenId}
              replyingToId={replyingToId}
              setReplyingToId={(id) => { setReplyingToId(id); setReplyText(''); setReplyError('') }}
              replyText={replyText}
              setReplyText={setReplyText}
              replyPosting={replyPosting}
              replyError={replyError}
              onStartEdit={(c) => { setEditingId(c.id); setEditText(c.content); setEditError('') }}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={handleSaveEdit}
              onDelete={handleDelete}
              onReply={handleReply}
              formatTime={formatTime}
              myAvatarUrl={myAvatarUrl}
              myAvatarInitial={myInitial}
              myAvatarGrad={myGrad}
              onReport={setReportingCommentId}
              likedSet={likedSet}
              likeCounts={likeCounts}
              onLike={handleLike}
            />
          )
        })}
      </div>

      {reportingCommentId && (
        <ReportModal
          contentType="comment"
          contentId={reportingCommentId}
          onClose={() => setReportingCommentId(null)}
        />
      )}
    </section>
  )
}

function QzCommentItem({
  c, replies, allComments = [], user, editingId, editText, setEditText, editError, saving,
  deleting, menuOpenId, setMenuOpenId, replyingToId, setReplyingToId,
  replyText, setReplyText, replyPosting, replyError,
  onStartEdit, onCancelEdit, onSaveEdit, onDelete, onReply, formatTime,
  myAvatarUrl, myAvatarInitial, myAvatarGrad, isReply = false, onReport,
  likedSet = new Set(), likeCounts = {}, onLike,
}) {
  const [showAllReplies, setShowAllReplies] = useState(false)
  const prof   = c.profile ?? {}
  const name   = prof.display_name || prof.username || 'Anonymous'
  const grad   = prof.avatar_grad  || 'linear-gradient(135deg, #7C3AED, #EC4899)'
  const imgUrl = prof.avatar_url   ?? null

  return (
    <div id={`comment-${c.id}`} className={isReply ? 'qz-comment qz-comment--reply' : 'qz-comment'}>
      <Link to={`/user/${prof.username}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
        <div className="qz-comment__avatar" style={{ background: imgUrl ? undefined : grad }}>
          {imgUrl
            ? <img src={imgUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : name[0]?.toUpperCase()}
        </div>
      </Link>
      <div className="qz-comment__body">
        <div className="qz-comment__header">
          <Link to={`/user/${prof.username}`} className="qz-comment__author" style={{ textDecoration: 'none', color: 'inherit' }}>{name}</Link>
          {isReply && c.reply_to_username && (
            <Link to={`/user/${c.reply_to_username}`} style={{ fontSize: '0.78rem', color: 'var(--text-faint)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              ↩ <span style={{ color: 'var(--purple)' }}>@{c.reply_to_username}</span>
            </Link>
          )}
          <span className="qz-comment__time">{formatTime(c.created_at)}</span>
          {user && user.id !== c.user_id && onReport && editingId !== c.id && (
            <button className="comment__report-btn" onClick={() => onReport(c.id)} aria-label="Report comment" title="Report comment">
              ⚑
            </button>
          )}
          {user?.id === c.user_id && editingId !== c.id && (
            <div className="comment__menu-wrap">
              <button className="comment__menu-btn" onClick={() => setMenuOpenId(id => id === c.id ? null : c.id)} aria-label="Comment options">
                •••
              </button>
              {menuOpenId === c.id && (
                <div className="comment__menu" onMouseDown={e => e.stopPropagation()}>
                  <button onClick={() => { setMenuOpenId(null); onStartEdit(c) }}>Edit</button>
                  <button className="comment__menu-delete" onClick={() => { setMenuOpenId(null); onDelete(c.id) }} disabled={deleting === c.id}>
                    {deleting === c.id ? '…' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <ResultBadge snapshot={c.result_snapshot} />

        {editingId === c.id ? (
          <div className="comment__edit-form">
            {editError && <div className="comment-form__error">{editError}</div>}
            <textarea className="comment-form__input" value={editText} onChange={e => setEditText(e.target.value)} rows={3} autoFocus />
            <div className="comment__edit-actions">
              <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={() => onSaveEdit(c.id)} disabled={!editText.trim() || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={onCancelEdit} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <p className="qz-comment__text">{decodeEntities(c.content)}</p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {editingId !== c.id && (
            <button
              className={`comment__like${likedSet.has(c.id) ? ' comment__like--active' : ''}`}
              onClick={() => onLike && onLike(c.id)}
              disabled={!user}
            >
              {likedSet.has(c.id) ? '\u2665' : '\u2661'}{(likeCounts[c.id] ?? 0) > 0 ? ` ${likeCounts[c.id]}` : ''}
            </button>
          )}
          {user && editingId !== c.id && (
            <button
              className="comment__reply-btn"
              style={{ cursor: 'pointer', zIndex: 1, position: 'relative' }}
              onClick={(e) => { e.stopPropagation(); setReplyingToId(replyingToId === c.id ? null : c.id) }}
            >
              {replyingToId === c.id ? 'Cancel' : 'Reply'}
            </button>
          )}
        </div>

        {replyingToId === c.id && (
          <div className="comment__reply-form">
            <div className="comment__reply-avatar" style={{ background: myAvatarUrl ? undefined : myAvatarGrad }}>
              {myAvatarUrl
                ? <img src={myAvatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : myAvatarInitial}
            </div>
            <div className="comment__reply-body">
              {replyError && <div className="comment-form__error">{replyError}</div>}
              <textarea
                className="comment-form__input"
                placeholder={`Reply to ${name}…`}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                rows={2}
                autoFocus
              />
              <div className="comment__edit-actions">
                <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={() => onReply(c.id)} disabled={!replyText.trim() || replyPosting}>
                  {replyPosting ? 'Posting…' : 'Post reply'}
                </button>
                <button className="btn" style={{ padding: '6px 16px', fontSize: '0.82rem' }} onClick={() => setReplyingToId(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {replies.length > 0 && (
          <div className="comment__replies">
            {!showAllReplies && replies.length > 2 && (
              <button className="comment__show-more" onClick={() => setShowAllReplies(true)}>
                Show {replies.length - 2} more {replies.length - 2 === 1 ? 'reply' : 'replies'}
              </button>
            )}
            {(showAllReplies ? replies : replies.slice(-2)).map(r => (
              <QzCommentItem
                key={r.id}
                c={r}
                replies={[]}
                user={user}
                editingId={editingId}
                editText={editText}
                setEditText={setEditText}
                editError={editError}
                saving={saving}
                deleting={deleting}
                menuOpenId={menuOpenId}
                setMenuOpenId={setMenuOpenId}
                replyingToId={replyingToId}
                setReplyingToId={setReplyingToId}
                replyText={replyText}
                setReplyText={setReplyText}
                replyPosting={replyPosting}
                replyError={replyError}
                onStartEdit={onStartEdit}
                onCancelEdit={onCancelEdit}
                onSaveEdit={onSaveEdit}
                onDelete={onDelete}
                onReply={onReply}
                formatTime={formatTime}
                myAvatarUrl={myAvatarUrl}
                myAvatarInitial={myAvatarInitial}
                myAvatarGrad={myAvatarGrad}
                onReport={onReport}
                likedSet={likedSet}
                likeCounts={likeCounts}
                onLike={onLike}
                isReply
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function QuizPlayerPage() {
  const { id }    = useParams()
  const navigate  = useNavigate()
  const { user, profile }  = useAuth()
  const toast = useToast()
  const currentUser = displayUser(user, profile)

  const [quiz, setQuiz]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [screen, setScreen]     = useState('intro')
  const [questionIdx, setQuestionIdx] = useState(0)
  const [answers, setAnswers]   = useState({})
  const [selected, setSelected] = useState(null)
  const [confirmed, setConfirmed] = useState(false)
  // Per-question shuffled answer order. Keyed by question.id → answers[]. Built
  // once at play start so Back navigation shows the same order, never re-shuffles.
  const [displayOrder, setDisplayOrder] = useState({})

  const [serverResult, setServerResult] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [saved,    setSaved]    = useState(false)
  const [saveBusy, setSaveBusy] = useState(false)
  const [reportingQuiz, setReportingQuiz] = useState(false)
  const [copied, setCopied]     = useState(false)
  const [hideBusy, setHideBusy] = useState(false)

  const isAdmin = profile?.isAdmin === true

  function handleShare() {
    navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  useEffect(() => {
    async function load() {
      try {
        const headers = await getHeaders()
        const r = await axios.get(`/api/quizzes/${id}`, { headers })
        setQuiz(r.data)
      } catch {
        setError('Quiz not found.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  // Load saved status once user + quiz are ready
  useEffect(() => {
    if (!currentUser || !id) return
    getHeaders()
      .then(headers => axios.get(`/api/profile/quiz-bookmarks/check/${id}`, { headers }))
      .then(r => setSaved(r.data.bookmarked))
      .catch(err => console.error('[QuizPlayerPage] Failed to check quiz bookmark state:', err))
  }, [currentUser?.username, id])

  async function handleSave() {
    if (saveBusy) return
    setSaveBusy(true)
    try {
      const headers = await getHeaders()
      if (saved) {
        await axios.delete(`/api/profile/quiz-bookmarks/${id}`, { headers })
        setSaved(false)
      } else {
        await axios.post(`/api/profile/quiz-bookmarks/${id}`, {}, { headers })
        setSaved(true)
      }
    } catch (err) { console.error('[QuizPlayerPage] Failed to toggle quiz bookmark:', err) } finally {
      setSaveBusy(false)
    }
  }

  const isOwner = user && quiz && user.id === quiz.userId

  async function handleDeleteQuiz() {
    if (!window.confirm('Delete this quiz? This cannot be undone.')) return
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/quizzes/${id}`, { headers })
      navigate('/quizzes')
    } catch {
      toast('Failed to delete quiz.', 'error')
    }
  }

  function handleStart() {
    ;(async () => {
      try {
        const headers = await getHeaders()
        await axios.post(`/api/quizzes/${id}/play`, null, { headers })
      } catch (err) {
        console.error('[QuizPlayerPage] Failed to increment play count:', err)
      }
    })()
    // Build per-question display order once at play start. When randomizeAnswers
    // is on, Fisher-Yates shuffle each question's answers. Stored by question id
    // so Back navigation reuses the same order — never re-shuffles mid-play.
    const order = {}
    for (const q of quiz.questions) {
      const arr = [...q.answers]
      if (quiz.randomizeAnswers) {
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[arr[i], arr[j]] = [arr[j], arr[i]]
        }
      }
      order[q.id] = arr
    }
    setDisplayOrder(order)
    setScreen('question')
  }

  // Pre-fill selection state when entering a question, so Back navigation
  // restores the user's prior answer.
  useEffect(() => {
    if (screen !== 'question' || !quiz) return
    const q = quiz.questions[questionIdx]
    const stored = answers[q.id]
    if (stored === undefined) {
      setSelected(null)
      setConfirmed(false)
    } else if (Array.isArray(stored)) {
      setSelected(stored)
      setConfirmed(true)
    } else {
      setSelected(stored)
      setConfirmed(false)
    }
    // We intentionally exclude `answers` from deps — re-running on every
    // answer mutation would clobber the user's in-progress selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIdx, screen, quiz?.id])

  function handleSelect(answerId) {
    const question = quiz.questions[questionIdx]
    const multiCorrect = question.multiCorrect ?? false

    if (multiCorrect) {
      // Toggle in/out of selection array
      setSelected(prev => {
        const arr = Array.isArray(prev) ? prev : []
        const next = arr.includes(answerId) ? arr.filter(id => id !== answerId) : [...arr, answerId]
        // If we're editing a previously-answered multi question, keep
        // `answers` in sync so the edit isn't lost without re-confirming.
        if (Array.isArray(answers[question.id])) {
          setAnswers(a => ({ ...a, [question.id]: next }))
        }
        return next
      })
    } else {
      // Single answer — immediate select
      setSelected(answerId)
      setAnswers(prev => ({ ...prev, [question.id]: answerId }))
    }
  }

  function handleConfirm() {
    const question = quiz.questions[questionIdx]
    setAnswers(prev => ({ ...prev, [question.id]: Array.isArray(selected) ? selected : [selected] }))
    setConfirmed(true)
  }

  function handleBack() {
    if (questionIdx === 0) return
    // Persist any in-progress edit on the current question before stepping back.
    const question = quiz.questions[questionIdx]
    if (selected !== null && (!Array.isArray(selected) || selected.length > 0)) {
      setAnswers(prev => ({ ...prev, [question.id]: selected }))
    }
    setQuestionIdx(i => i - 1)
  }

  async function handleNext() {
    const question = quiz.questions[questionIdx]
    // Persist current selection before advancing/submitting (covers edits made
    // on revisited questions, which don't always flow through handleSelect).
    let mergedAnswers = answers
    if (selected !== null && (!Array.isArray(selected) || selected.length > 0)) {
      mergedAnswers = { ...answers, [question.id]: selected }
      setAnswers(mergedAnswers)
    }
    if (questionIdx + 1 >= quiz.questions.length) {
      setSubmitting(true)
      try {
        const headers = await getHeaders()
        const res = await axios.post(`/api/quizzes/${id}/submit`, { answers: mergedAnswers }, { headers })
        setServerResult(res.data)
        setScreen('results')
      } catch (err) {
        console.error('[QuizPlayerPage] submit failed:', err)
        setError('Failed to submit quiz. Please try again.')
      } finally {
        setSubmitting(false)
      }
    } else {
      setQuestionIdx(i => i + 1)
    }
  }

  if (loading) return <><Navbar /><div className="qz-loading">Loading quiz…</div></>
  if (error)   return <><Navbar /><div className="qz-loading">{error} <Link to="/quizzes">← Back</Link></div></>

  // Build result snapshot for the comments section from server result
  let resultSnapshot = null
  if (screen === 'results' && serverResult) {
    if (serverResult.type === 'knowledge') {
      const { score, total, pct } = serverResult
      const verdict = pct === 100 ? '🏆 Perfect score!' : pct >= 80 ? '🌟 Great job!' : pct >= 60 ? '👍 Not bad!' : pct >= 40 ? '📚 Keep studying!' : '💪 Better luck next time!'
      resultSnapshot = { type: 'knowledge', score, total, pct, verdict }
    } else {
      resultSnapshot = { type: 'personality', outcome: serverResult.outcome?.title ?? 'Unknown' }
    }
  }

  return (
    <div className="qz-page">
      <SEO
        title={decodeEntities(quiz.title)}
        description={decodeEntities(quiz.description?.slice(0, 160) || `Take the quiz "${quiz.title}" by ${quiz.author} on Verse.`)}
        image={quiz.coverUrl}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Quiz',
          name: quiz.title,
          author: { '@type': 'Person', name: quiz.author },
          description: quiz.description?.slice(0, 300),
        }}
      />
      <Navbar />
      {isOwner && (
        <div className="qz-owner-bar">
          <span className="qz-owner-bar__label">Your quiz</span>
          <div className="qz-owner-bar__actions">
            <button className="qz-owner-bar__btn" onClick={() => navigate(`/quiz/edit/${id}`)}>Edit</button>
            {quiz.published && (
              <button
                className="qz-owner-bar__btn"
                onClick={async () => {
                  if (!window.confirm('Unpublish this quiz? It will be removed from the quizzes page and only visible to you.')) return
                  try {
                    const headers = await getHeaders()
                    await axios.put(`/api/quizzes/${id}`, { published: false }, { headers })
                    navigate('/quizzes')
                  } catch (err) {
                    console.error('[unpublish quiz]', err)
                    toast('Failed to unpublish quiz.', 'error')
                  }
                }}
              >
                Unpublish
              </button>
            )}
            <button className="qz-owner-bar__btn qz-owner-bar__btn--danger" onClick={handleDeleteQuiz}>Delete</button>
          </div>
        </div>
      )}
      <div className="qz-container">
        {screen === 'intro'    && (
          <IntroScreen
            quiz={quiz}
            onStart={handleStart}
            saved={saved}
            onSave={currentUser ? handleSave : null}
            saveBusy={saveBusy}
            onReport={currentUser && !isOwner ? () => setReportingQuiz(true) : null}
            onShare={handleShare}
            copied={copied}
            isAdmin={isAdmin}
            hideBusy={hideBusy}
            onHide={isAdmin ? async () => {
              setHideBusy(true)
              try {
                const headers = await getHeaders()
                await axios.patch(`/api/admin/hide/quiz/${id}`, { hidden: !quiz.hidden }, { headers })
                setQuiz(prev => ({ ...prev, hidden: !prev.hidden }))
              } catch (err) { console.error('[QuizPlayerPage] Failed to toggle quiz visibility:', err) }
              setHideBusy(false)
            } : null}
          />
        )}
        {screen === 'question' && (
          <QuestionScreen
            quiz={quiz}
            questionIdx={questionIdx}
            selected={selected}
            onSelect={handleSelect}
            onConfirm={handleConfirm}
            confirmed={confirmed}
            onNext={handleNext}
            onBack={handleBack}
            isRevisit={answers[quiz.questions[questionIdx].id] !== undefined}
            displayAnswers={displayOrder[quiz.questions[questionIdx].id]}
          />
        )}
        {screen === 'results'  && serverResult && (
          <ResultsScreen
            quiz={quiz}
            serverResult={serverResult}
            saved={saved}
            onSave={currentUser ? handleSave : null}
            saveBusy={saveBusy}
          />
        )}
        {submitting && (
          <div className="qz-loading">Calculating results…</div>
        )}
        {screen !== 'question' && !quiz.disableComments && (
          <QuizCommentsSection
            quizId={id}
            user={user}
            profile={profile}
            resultSnapshot={resultSnapshot}
          />
        )}
        {screen !== 'question' && quiz.disableComments && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-faint)', fontSize: '0.9rem' }}>
            Comments are disabled on this quiz.
          </div>
        )}
      </div>
      {reportingQuiz && (
        <ReportModal
          contentType="quiz"
          contentId={id}
          contentTitle={quiz?.title}
          onClose={() => setReportingQuiz(false)}
        />
      )}
    </div>
  )
}
